import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Table from 'react-bootstrap/Table';
import { getFirestore, Timestamp, doc, setDoc , collection, query, getDocs, where, orderBy, limit } from "@firebase/firestore";
import { app } from '../../firebase.js';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { useNavigate } from 'react-router-dom';
import { getAuth } from "firebase/auth";
import Pagination from 'react-bootstrap/Pagination';
import profilePictureContImage from '../../Images/defprofile.png';
import swal from 'sweetalert';
import { Link } from 'react-router-dom'; 
import * as XLSX from "xlsx";
import { buildReceiptEmail } from '../../utils/emailTemplates';
import { ORGANIZATION_NAME } from '../../branding';

const chunkArray = (items, chunkSize) => {
    const chunks = [];

    for (let index = 0; index < items.length; index += chunkSize) {
        chunks.push(items.slice(index, index + chunkSize));
    }

    return chunks;
};


const SearchComponent = ({ onSelectStudent }) => {
    const [searchValue, setSearchValue] = useState('');
    const [results, setResults] = useState([]);
    const searchResultsRef = useRef(null);
    const db = getFirestore(app);

    const handleSearchChange = async (event) => {
        const value = event.target.value;
        setSearchValue(value);

        if (value.trim() !== "") {
            const endValue = value + '\uf8ff';
            const q = query(collection(db, "users"), where("bio", ">=", value), where("bio", "<=", endValue));
            const querySnapshot = await getDocs(q);
            const searchResults = [];
            querySnapshot.forEach((doc) => {
                const profilePic = doc.data().profilePic || profilePictureContImage;
                const name = doc.data().bio;
                const userStatus = doc.data().userStatus;
                const userId = doc.data().userId;
                const userEmail = doc.data().userEmail;  // Ensure that email is stored in Firestore
                searchResults.push({ profilePic, bio: name, userStatus, userId,userEmail });
            });
            setResults(searchResults);
        } else {
            setResults([]);
        }
    };

    return (
        <Form.Group className="mb-3" controlId="formBasicEmail">
            <Form.Label>Search for a student</Form.Label>
            <Form.Control
                type="text"
                placeholder="Select student"
                value={searchValue}
                onChange={handleSearchChange}
            />
            {results.length > 0 && ( 
                <div className='searchResultsStud' ref={searchResultsRef}>
                    {results.map((result, index) => (
                        <div key={index} className='resultItem' onClick={() => onSelectStudent(result.userId)}>
                            <div className='resultItemDiv'>
                                <img src={result.profilePic || profilePictureContImage} alt={`${result.bio}'s profile`} />
                            </div>
                            <div>
                                <p>{result.bio}</p>
                                <p className='statusResult'>{result.userStatus}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Form.Group>
    );
};

const IncomeComp = () => {
    const [modalShowUp, setModalShowUp] = useState(false);
    const [loading, setLoading] = useState(true);
    const [studentsList, setStudentList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const recordsPerPage = 50;
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [amount, setAmount] = useState('');
    const [paymentDate, setPaymentDate] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('');
    const db = getFirestore(app);
    const auth = getAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const incomeRef = query(
                    collection(db, 'Income'),
                    orderBy("paymentDate", "desc")
                );
                const incomeSnapshot = await getDocs(incomeRef);
                const incomeRecords = incomeSnapshot.docs.map((incomeDoc) => ({
                    id: incomeDoc.id,
                    ...incomeDoc.data()
                }));

                if (!incomeRecords.length) {
                    setStudentList([]);
                    return;
                }

                const uniqueStudentIds = [...new Set(
                    incomeRecords
                        .map((income) => income.studentId)
                        .filter(Boolean)
                )];

                const studentChunks = chunkArray(uniqueStudentIds, 10);
                const studentSnapshots = await Promise.all(
                    studentChunks.map((studentIds) => getDocs(
                        query(
                            collection(db, 'users'),
                            where("userId", "in", studentIds)
                        )
                    ))
                );

                const studentMap = new Map();
                studentSnapshots.forEach((snapshot) => {
                    snapshot.forEach((studentDoc) => {
                        const studentData = studentDoc.data();
                        studentMap.set(studentData.userId, studentData);
                    });
                });

                const mergedIncomeData = incomeRecords.map((income) => {
                    const studentData = studentMap.get(income.studentId);

                    return {
                        userId: income.studentId,
                        bio: studentData?.bio || 'Unknown Student',
                        course: studentData?.course || '-',
                        userStatus: studentData?.userStatus || '-',
                        userEmail: studentData?.userEmail || '',
                        phoneNumber: studentData?.phoneNumber || '',
                        income
                    };
                });

                setStudentList(mergedIncomeData);
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [db]);

    const indexOfLastRecord = useMemo(() => currentPage * recordsPerPage, [currentPage, recordsPerPage]);
    const indexOfFirstRecord = useMemo(() => indexOfLastRecord - recordsPerPage, [indexOfLastRecord]);
    const currentRecords = useMemo(() => studentsList.slice(indexOfFirstRecord, indexOfLastRecord), [studentsList, indexOfFirstRecord, indexOfLastRecord]);
    const totalPages = useMemo(() => Math.ceil(studentsList.length / recordsPerPage), [studentsList.length, recordsPerPage]);
    const totalIncome = useMemo(() => studentsList.reduce((sum, item) => sum + parseFloat(item.income.incomeAmount || 0), 0), [studentsList]);
    const currentPageIncome = useMemo(() => currentRecords.reduce((sum, item) => sum + parseFloat(item.income.incomeAmount || 0), 0), [currentRecords]);
    const uniqueStudentsCount = useMemo(() => new Set(studentsList.map((item) => item.userId).filter(Boolean)).size, [studentsList]);
    const latestIncomeRecord = studentsList[0] || null;

    const formatAmount = useCallback((value) => (
        Number(value || 0).toLocaleString('en-KE')
    ), []);

    const formatDateTime = useCallback((timestamp) => {
        if (!timestamp?.seconds) {
            return '-';
        }

        const date = new Date(timestamp.seconds * 1000);
        return `${date.toLocaleDateString()} at ${date.toLocaleTimeString()}`;
    }, []);

    function generateReceiptNumber() {
        // Get the current date and time
        const now = new Date();
        
        // Extract year (last two digits) and month (two digits)
        const yearPart = now.getFullYear().toString().slice(-2);  // Get last two digits of the year
        const monthPart = String(now.getMonth() + 1).padStart(2, '0');  // Get month, pad with zero if needed
        
        // Generate a random 4-digit number
        const randomPart = Math.floor(1000 + Math.random() * 9000);  // Random number between 1000 and 9999
        
        // Combine yearPart, randomPart, and monthPart as numbers
        const receiptNumber = parseInt(`${yearPart}${randomPart}${monthPart}`, 10);
        
        return receiptNumber;
      }
      
  
 // console.log();
  
    
    const handleSubmit = async () => {
        if (!selectedStudent) {
            alert("Please select a student.");
            return;
        }
        
        if (!amount || !paymentDate || !paymentMethod) {
            alert("Please fill all fields.");
            return;
        }

        const paymentTimestamp = new Date(paymentDate).getTime();

        document.getElementById("submitBtn").style.display= "none";
        document.getElementById("spinnerbtn").style.display= "block";


        let invnumb = generateReceiptNumber()
        let userEmail = selectedStudent.userEmail
        let incomeAmount =  amount;
        let studentsUserName = selectedStudent.bio;

        const sendEmail = buildReceiptEmail({
            studentName: studentsUserName,
            amount: incomeAmount,
            paymentDate,
            paymentMethod,
            receiptNumber: invnumb
        });

        try {

          const newDataRef = doc(collection(db, "Income"));
          setDoc(newDataRef, {

              studentId: selectedStudent.userId,
                incomeAmount: amount,
                paymentDate: Timestamp.fromMillis(paymentTimestamp),
                paymentMethod: paymentMethod,
                createdBy: auth.currentUser.uid,
                timeCreated: Timestamp.now(),
                paymentId:newDataRef.id,
                invoiceNumber:generateReceiptNumber()

          }).then(()=>{

            const newMail = doc(collection(db, "mail"));
            setDoc(newMail, {
              to: userEmail,
              message: {
              subject: `${ORGANIZATION_NAME} Payment Receipt #${invnumb}`,
              text: 'Hi  user, ',
              html: sendEmail, 
              }
            });

            }).then(()=>{
              sendSMS(studentsUserName, amount, paymentDate, invnumb, selectedStudent.phoneNumber);
              setModalShowUp(false); // Close the modal on successful submission     
              // swal("Success!", "Income submitted successfully", "success");
              // window.location.reload()
            })

        } catch (error) {
            console.error("Error adding document: ", error);
            swal('Error!', 'An error occured!', 'error');
        }
    };

    const sendSMS = async (studentName, amount, paymentDate, receiptNumber, phoneNumber) => {
      const message = `Dear ${studentName}, we have received your payment of KES. ${amount} on ${paymentDate} . View your receipt here https://live.ubunifucollege.com//view-receipt?${receiptNumber}. Thank you for your timely payment. - Nutrition And Dietetics Academy`;
      const smsData = {
          phoneNumber: phoneNumber,
          message: message
      }; 

      try {
          const response = await fetch('https://us-central1-ubunifu-college.cloudfunctions.net/api/send-sms', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(smsData)
          });

          const data = await response.json();
          console.log(data);
          swal("Success!", "Income submitted successfully", "success");
          window.location.reload();
          return true;
      } catch (error) {
          console.error("Error sending SMS: ", error);
          swal("Success!", "Income submitted successfully", "success");
          window.location.reload();
          return false;
      }
    };


    const MyUpdateModal = useMemo(() => (props) => {
        return (
            <Modal
                {...props}
                aria-labelledby="contained-modal-title-vcenter"
                centered>
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">
                        Add an Income
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <SearchComponent onSelectStudent={(userId) => selectStudent(userId)} />

                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Status</th>
                                <th>Course</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                        {selectedStudent && (
                                <tr>
                                    <td>{selectedStudent.bio}</td>
                                    <td>{selectedStudent.userStatus}</td>
                                    <td>{selectedStudent.course}</td>
                                    <td>
                                        <button className='btn btn-sm btn-primary' onClick={() => viewStudent(selectedStudent.userId)}>View</button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    <Form.Group className="mb-3" controlId="formBasicEmail">
                        <Form.Label>Selected Student</Form.Label>
                        <Form.Control type="text" value={selectedStudent ? selectedStudent.bio : ''} disabled />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formBasicEmail">
                        <Form.Label>Amount</Form.Label>
                        <Form.Control 
                            type="number" 
                            value={amount} 
                            onChange={(e) => setAmount(e.target.value)} 
                        />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="formBasicEmail">
                        <Form.Label>Payment Date</Form.Label>
                        <Form.Control 
                            type="datetime-local" 
                            value={paymentDate} 
                            onChange={(e) => setPaymentDate(e.target.value)} 
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formBasicEmail">
                        <Form.Label>Payment Method</Form.Label>
                        <Form.Select 
                            aria-label="Default select example" 
                            value={paymentMethod} 
                            onChange={(e) => setPaymentMethod(e.target.value)}
                        >
                            <option>Select payment method</option>
                            <option value="Mpesa">Mpesa</option>
                            <option value="Bank Transfer">Bank Transfer</option>
                            <option value="Cash">Cash</option>
                            <option value="Cheque">Cheque</option>
                            <option value="Bank Account Deposit">Bank Account Deposit</option>
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button className='btn btn-dark' onClick={props.onHide}>Close</Button>
                    <Button 
                        variant="primary" 
                        id='submitBtn' 
                        onClick={handleSubmit}
                    >
                        Send
                    </Button> 
                    <Button 
                        variant="primary" 
                        disabled 
                        id='spinnerbtn' 
                        style={{ display: 'none' }}
                    >
                        <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                        />
                        <span> Sending...</span>
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }, [selectedStudent, amount, paymentDate, paymentMethod]);

    const selectStudent = useCallback(async (userId) => {
        try {
            const studentRef = query(
                collection(db, 'users'),
                where("userId", "==", userId),
                limit(1)
            );
            const studentSnapshot = await getDocs(studentRef);

            if (!studentSnapshot.empty) {
                setSelectedStudent(studentSnapshot.docs[0].data());
            }
        } catch (error) {
            console.error("Error selecting student:", error);
        }
    }, [db]);

    const viewStudent = useCallback((userId) => {
        navigate('/view-student', { state: { myData: userId } });
    }, [navigate]);

    const viewReceipt = useCallback((incomeId) => {
        window.location.href = "/view-receipt" +"?" +  incomeId;
        //navigate('/view-receipt', { state: { myData: incomeId } });
    }, [navigate]);

    const handlePageChange = useCallback((pageNumber) => {
        setCurrentPage(pageNumber);
    }, []);

    const exportToExcel = () => {
    if (!studentsList.length) {
        alert("No data to export");
        return;
    }

    // Prepare plain JSON
    const exportData = studentsList.map((item) => ({
        "Payment Date": new Date(item.income.paymentDate.seconds * 1000).toLocaleString(),
        "Student Name": item.bio,
        "Course": item.course,
        "Amount (KES)": item.income.incomeAmount,
        "Payment Method": item.income.paymentMethod,
        "Receipt No": item.income.invoiceNumber || item.income.id
    }));

    // Convert to worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Income");

    // Export file
    XLSX.writeFile(workbook, "income_records.xlsx");
};
 

    return (
        <div className='clientsComp incomePage'>
            <div className="incomeHero">
                <div className="incomeHeroCopy">
                    <div className="incomeHeroTitleRow">
                        <h5>School Fee Income</h5>
                    </div>
                    <p className="incomeHeaderMeta">
                        <span>{studentsList.length} records</span>
                        <span className="incomeInlineDot" />
                        <span>{uniqueStudentsCount} students</span>
                        <span className="incomeInlineDot" />
                        <span>Newest first</span>
                    </p>
                </div>
                <div className="incomeHeroActions">
                    <button onClick={exportToExcel} className="btn btn-outline-dark">
                        Export to Excel
                    </button>
                    <Link to="/add-income" className="btn btn-dark">Add a Fee Income</Link>
                </div>
            </div>

            <div className="incomeStatsGrid">
                <div className="incomeStatCard incomeStatPrimary">
                    <span className="incomeStatLabel">Total Recorded Income</span>
                    <h3>KES {formatAmount(totalIncome)}</h3>
                    <p>All payments currently loaded.</p>
                </div>
                <div className="incomeStatCard">
                    <span className="incomeStatLabel">Students Covered</span>
                    <h3>{uniqueStudentsCount}</h3>
                    <p>Unique students with income records.</p>
                </div>
                <div className="incomeStatCard">
                    <span className="incomeStatLabel">This Page Total</span>
                    <h3>KES {formatAmount(currentPageIncome)}</h3>
                    <p>{currentRecords.length} records in this view.</p>
                </div>
                <div className="incomeStatCard">
                    <span className="incomeStatLabel">Latest Recorded Income</span>
                    <h3>{latestIncomeRecord ? `KES ${formatAmount(latestIncomeRecord.income.incomeAmount)}` : 'No records'}</h3>
                    <p>{latestIncomeRecord ? `${latestIncomeRecord.bio} • ${formatDateTime(latestIncomeRecord.income.paymentDate)}` : 'New records will appear here.'}</p>
                </div>
            </div>

            <div className="searchContainer incomeToolbar">
                <div className="incomeToolbarMeta">
                    <span className="incomeToolbarText">Page {currentPage} of {totalPages || 1}</span>
                </div>
                <div className="incomeToolbarMeta">
                    <span className="incomeToolbarText">Showing {currentRecords.length} of {studentsList.length} records</span>
                </div>
            </div>

            {loading ? (
                <div className="incomeStateCard">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p>Loading the latest fee income records...</p>
                </div>
            ) : (
                <>
                    {studentsList.length === 0 ? (
                        <div className="incomeStateCard incomeEmptyState">
                            <h6>No income records yet</h6>
                            <p>Once a school fee payment is recorded, it will appear here with the newest payment at the top.</p>
                            <Link to="/add-income" className="btn btn-primary">Record the First Fee Income</Link>
                        </div>
                    ) : (
                        <>
                            <div className="incomeTableCard">
                                <div className="incomeTableHeader">
                                    <div>
                                        <h6>Recorded Payments</h6>
                                        <p>Recent school fee income records.</p>
                                    </div>
                                </div>
                                <div className="table-responsive">
                                    <Table hover className="align-middle incomeRecordsTable">
                                        <thead>
                                            <tr>
                                                <th>Payment Date</th>
                                                <th>Student Name</th>
                                                <th>Course</th>
                                                <th>Amount</th>
                                                <th>Payment Method</th>
                                                <th>View Receipt</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentRecords.map((mystudent) => (
                                                <tr key={mystudent.income.id}>
                                                    <td>
                                                        <div className="incomeDateCell">
                                                            <strong>{formatDateTime(mystudent.income.paymentDate)}</strong>
                                                            <span>Receipt #{mystudent.income.invoiceNumber || '-'}</span>
                                                        </div>
                                                    </td>
                                                    <td onClick={() => viewStudent(mystudent.userId)}>
                                                        <span className='viewStudentSpan incomeStudentName'>{mystudent.bio}</span>
                                                    </td>
                                                    <td>
                                                        <span className="incomeCoursePill">{mystudent.course}</span>
                                                    </td>
                                                    <td>
                                                        <span className="incomeAmountText">KES {formatAmount(mystudent.income.incomeAmount)}</span>
                                                    </td>
                                                    <td>
                                                        <span className="incomeMethodBadge">{mystudent.income.paymentMethod}</span>
                                                    </td>
                                                    <td>
                                                        <button className='btn btn-sm btn-primary incomeReceiptButton' onClick={() => viewReceipt(mystudent.income.invoiceNumber)}>
                                                            View receipt
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                            <div className="incomePaginationWrap">
                                <Pagination>
                                    {[...Array(totalPages)].map((_, index) => (
                                        <Pagination.Item key={index + 1} active={index + 1 === currentPage} onClick={() => handlePageChange(index + 1)}>
                                            {index + 1}
                                        </Pagination.Item>
                                    ))}
                                </Pagination>
                            </div>
                        </>
                    )}
                </>
            )}

            <MyUpdateModal show={modalShowUp} onHide={() => setModalShowUp(false)} />
        </div>
    );
};

export default IncomeComp;
